// src/components/Box/EditModal.js
import React, { useState, useMemo } from "react";
import IncomeTable from "../Income/IncomeTable";
import EntityFinancialsTable from "../EntityFinancials/EntityFinancialsTable";
import TaxSummary from "../TaxSummary/TaxSummary";
import { calculateFiscalParts, MARITAL_STATUS_OPTIONS, EMPLOYMENT_TYPE_OPTIONS } from "../../utils/fiscalCalculations";
import "./EditModal.css";

const EditModal = ({
  fields,
  data,
  onSave,
  onClose,
  isIndividual,
  individualId,
  individualName,
  isEntity,
  entityId,
  entityName,
  taxRegime,
  entities = []
}) => {
  const [editedData, setEditedData] = useState({
    ...data,
    first_name: data.first_name || data.firstName || '',
    last_name: data.last_name || data.lastName || '',
    marital_status: data.marital_status || data.maritalStatus || 'Célibataire',
    children_count: data.children_count || data.childrenCount || 0,
    single_parent: data.single_parent || data.singleParent || false,
    employment_type: data.employment_type || data.employmentType || '',
    fiscal_residence: data.fiscal_residence || data.fiscalResidence || 'France'
  });
  const [activeTab, setActiveTab] = useState('info');

  const handleChange = (field, value) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChildrenChange = (delta) => {
    setEditedData(prev => ({
      ...prev,
      children_count: Math.max(0, (prev.children_count || 0) + delta)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(editedData);
  };

  const fiscalParts = useMemo(() => calculateFiscalParts(editedData), [editedData]);
  const showSingleParent = ['Célibataire', 'Divorcé', 'Veuf'].includes(editedData.marital_status) && editedData.children_count > 0;

  const showIndividualTabs = isIndividual && individualId;
  const showEntityTabs = isEntity && entityId;
  const showTabs = showIndividualTabs || showEntityTabs;

  const displayName = individualName || entityName || data.entity_name || data.content;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content edit-modal ${showTabs ? 'modal-large-tabs' : ''}`} onClick={e => e.stopPropagation()}>
        <h2>Modifier : {displayName}</h2>

        {showTabs && (
          <div className="modal-tabs">
            <button
              className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              type="button"
            >
              Informations
            </button>
            {showIndividualTabs && (
              <>
                <button
                  className={`tab-btn ${activeTab === 'incomes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('incomes')}
                  type="button"
                >
                  Revenus
                </button>
                <button
                  className={`tab-btn tab-btn-tax ${activeTab === 'tax' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tax')}
                  type="button"
                >
                  Impôt
                </button>
              </>
            )}
            {showEntityTabs && (
              <button
                className={`tab-btn tab-btn-entity ${activeTab === 'financials' ? 'active' : ''}`}
                onClick={() => setActiveTab('financials')}
                type="button"
              >
                Finances
              </button>
            )}
          </div>
        )}

        {activeTab === 'info' && isIndividual && (
          <form onSubmit={handleSubmit} className="person-info-form">
            {/* Identité */}
            <div className="form-section">
              <h4><span className="section-icon">👤</span> Identité</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={editedData.first_name}
                    onChange={e => handleChange('first_name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={editedData.last_name}
                    onChange={e => handleChange('last_name', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Résidence fiscale</label>
                <select
                  value={editedData.fiscal_residence}
                  onChange={e => handleChange('fiscal_residence', e.target.value)}
                >
                  <option value="France">France</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
            </div>

            {/* Situation Familiale */}
            <div className="form-section">
              <h4><span className="section-icon">👨‍👩‍👧‍👦</span> Situation Familiale</h4>
              <div className="form-group">
                <label>Statut marital</label>
                <div className="status-cards">
                  {MARITAL_STATUS_OPTIONS.map(opt => (
                    <div
                      key={opt.value}
                      className={`status-card ${editedData.marital_status === opt.value ? 'selected' : ''}`}
                      onClick={() => handleChange('marital_status', opt.value)}
                    >
                      <span className="status-icon">{opt.icon}</span>
                      <span className="status-label">{opt.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Enfants à charge</label>
                <div className="children-control">
                  <button
                    type="button"
                    className="children-btn minus"
                    onClick={() => handleChildrenChange(-1)}
                    disabled={editedData.children_count === 0}
                  >−</button>
                  <span className="children-count">{editedData.children_count}</span>
                  <button
                    type="button"
                    className="children-btn plus"
                    onClick={() => handleChildrenChange(1)}
                  >+</button>
                </div>
              </div>

              {showSingleParent && (
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editedData.single_parent}
                      onChange={e => handleChange('single_parent', e.target.checked)}
                    />
                    <span>Parent isolé (majoration de 0.5 part)</span>
                  </label>
                </div>
              )}

              <div className="fiscal-parts-display">
                <span className="label">Quotient familial</span>
                <span className="parts">{fiscalParts} part{fiscalParts > 1 ? 's' : ''}</span>
              </div>
            </div>

            {/* Activité */}
            <div className="form-section">
              <h4><span className="section-icon">💼</span> Activité principale</h4>
              <div className="employment-cards">
                {EMPLOYMENT_TYPE_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={`employment-card ${editedData.employment_type === opt.value ? 'selected' : ''}`}
                    onClick={() => handleChange('employment_type', opt.value)}
                  >
                    <span className="employment-icon">{opt.icon}</span>
                    <span className="employment-label">{opt.label}</span>
                    {opt.description && <span className="employment-description">{opt.description}</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="button-group">
              <button type="button" className="cancel-btn" onClick={onClose}>Annuler</button>
              <button type="submit" className="save-btn">Enregistrer</button>
            </div>
          </form>
        )}

        {activeTab === 'info' && isEntity && (
          <form onSubmit={handleSubmit}>
            <div className="form-fields">
              {fields.map((field) => (
                <div key={field.name} className="form-group">
                  <label htmlFor={field.name}>{field.label}</label>
                  <input
                    id={field.name}
                    name={field.name}
                    value={editedData[field.name] || ""}
                    onChange={e => handleChange(field.name, e.target.value)}
                    type={field.type}
                  />
                </div>
              ))}
            </div>
            <div className="button-group">
              <button type="button" className="cancel-btn" onClick={onClose}>Annuler</button>
              <button type="submit" className="save-btn">Enregistrer</button>
            </div>
          </form>
        )}

        {activeTab === 'info' && !isIndividual && !isEntity && (
          <form onSubmit={handleSubmit}>
            <div className="form-fields">
              {fields.map((field) => (
                <div key={field.name} className="form-group">
                  <label htmlFor={field.name}>{field.label}</label>
                  <input
                    id={field.name}
                    name={field.name}
                    value={editedData[field.name] || ""}
                    onChange={e => handleChange(field.name, e.target.value)}
                    type={field.type}
                  />
                </div>
              ))}
            </div>
            <div className="button-group">
              <button type="button" className="cancel-btn" onClick={onClose}>Annuler</button>
              <button type="submit" className="save-btn">Enregistrer</button>
            </div>
          </form>
        )}

        {activeTab === 'incomes' && isIndividual && individualId && (
          <div className="incomes-tab-content">
            <IncomeTable
              individualId={individualId}
              individualName={individualName}
              entities={entities}
            />
            <div className="button-group">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        )}

        {activeTab === 'tax' && isIndividual && individualId && (
          <div className="tax-tab-content">
            <TaxSummary
              individual={data}
              individualId={individualId}
              individualName={individualName}
            />
            <div className="button-group">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        )}

        {activeTab === 'financials' && isEntity && entityId && (
          <div className="financials-tab-content">
            <EntityFinancialsTable
              entityId={entityId}
              entityName={entityName}
              taxRegime={taxRegime}
            />
            <div className="button-group">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditModal;
